/**
 * "Keep same size on screen" for hotspots.
 *
 * A hotspot is a mesh in the scene, so moving or zooming the camera closer makes it bigger on
 * screen. In this mode its set size is what it shows at a reference camera distance; at any other
 * distance its scale is multiplied by distance / reference (and by the field-of-view change), so it
 * keeps that on-screen size. Creators can cap how far the multiplier goes either way.
 *
 * The viewer applies the multiplier to the entity's local scale. Other code also writes that scale
 * (creation, async media sizing, editor edits, entity animations), so each axis that no longer holds
 * the value applied last is taken as a new set size on that axis. Comparing per axis matters: an
 * animation that drives one axis writes the current, already multiplied, values back on the others,
 * and treating those as new set sizes would grow them every frame.
 */

export const SCREEN_SIZE_DEFAULT_DISTANCE = 5;
export const SCREEN_SIZE_DEFAULT_MIN = 0.1;
export const SCREEN_SIZE_DEFAULT_MAX = 10;

export interface ScreenSizeFields {
    keepScreenSize?: boolean;
    screenSizeDistance?: number;
    screenSizeMin?: number;
    screenSizeMax?: number;
}

export interface ScreenSizeSettings {
    /** Camera distance, in scene units, at which the hotspot shows its set size. */
    distance: number;
    /** Smallest multiple of the set size. */
    min: number;
    /** Largest multiple of the set size. */
    max: number;
}

// typeof check too: scene JSON is not type-checked.
const positiveOr = (value: number | undefined, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

/** The hotspot's screen-size settings, or null when the mode is off. Bad values fall back to defaults. */
export function resolveScreenSizeSettings(fields: ScreenSizeFields | null | undefined): ScreenSizeSettings | null {
    if (!fields?.keepScreenSize) return null;
    const distance = positiveOr(fields.screenSizeDistance, SCREEN_SIZE_DEFAULT_DISTANCE);
    let min = positiveOr(fields.screenSizeMin, SCREEN_SIZE_DEFAULT_MIN);
    let max = positiveOr(fields.screenSizeMax, SCREEN_SIZE_DEFAULT_MAX);
    if (min > max) [min, max] = [max, min];
    return { distance, min, max };
}

/**
 * Multiplier that keeps the set size on screen: distance / reference distance, times the change in
 * the camera's field of view (tan of half the vertical FOV against the scene's default FOV), clamped
 * to the settings' limits.
 */
export function screenSizeFactor(
    cameraDistance: number,
    settings: ScreenSizeSettings,
    fovDegrees?: number,
    referenceFovDegrees?: number,
): number {
    let raw = cameraDistance / settings.distance;
    if (fovDegrees && referenceFovDegrees && fovDegrees > 0 && referenceFovDegrees > 0 && fovDegrees < 180 && referenceFovDegrees < 180) {
        const half = Math.PI / 360;
        raw *= Math.tan(fovDegrees * half) / Math.tan(referenceFovDegrees * half);
    }
    if (!Number.isFinite(raw)) return settings.max;
    return Math.min(settings.max, Math.max(settings.min, raw));
}

export type Vec3Tuple = [number, number, number];

export interface ScreenSizeState {
    /** The set size (local scale without the multiplier). */
    base: Vec3Tuple;
    /** The local scale written last. */
    applied: Vec3Tuple;
    /** The multiplier in `applied`. */
    factor: number;
}

interface ScaleLike {
    x: number;
    y: number;
    z: number;
}

/**
 * One step: pick up axes someone else rewrote as the new set size, then apply `factor`. `changed`
 * is false when the entity already holds the result, so a still camera writes nothing (on-demand
 * rendering stays asleep).
 */
export function stepScreenSize(
    current: ScaleLike,
    state: ScreenSizeState | null,
    factor: number,
): { state: ScreenSizeState; scale: Vec3Tuple; changed: boolean } {
    const now: Vec3Tuple = [current.x, current.y, current.z];
    const base: Vec3Tuple = state
        ? [0, 1, 2].map((i) => (now[i] === state.applied[i] ? state.base[i] : now[i])) as Vec3Tuple
        : now;
    const scale = base.map((v) => v * factor) as Vec3Tuple;
    const changed = scale.some((v, i) => v !== now[i]);
    return { state: { base, applied: scale, factor }, scale, changed };
}

/** The local scale to restore when the mode is switched off (keeps any axis rewritten since). */
export function releaseScreenSize(current: ScaleLike, state: ScreenSizeState): Vec3Tuple {
    const now: Vec3Tuple = [current.x, current.y, current.z];
    return [0, 1, 2].map((i) => (now[i] === state.applied[i] ? state.base[i] : now[i])) as Vec3Tuple;
}
